import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from "firebase/functions";
import { functions, auth } from './firebase';
import { onAuthStateChanged } from "firebase/auth";
import './GalleryPage.css';

// Helper to format distance
const formatDistance = (meters) => {
    if (!meters || isNaN(meters)) return 'N/A';
    const km = meters / 1000;
    return km < 1 ? `${Math.round(meters)}m` : `${km.toFixed(1)}km`;
};

// Helper to format elevation
const formatElevation = (meters) => {
    if (!meters || isNaN(meters)) return 'N/A';
    return `${Math.round(meters)}m`;
};

// Helper to format duration
const formatDuration = (totalSeconds) => {
    if (!totalSeconds || isNaN(totalSeconds)) return 'N/A';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

// Map Card Component with slider overlay
function MapCard({ map }) {
    const [sliderValue, setSliderValue] = useState(90);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStarted, setDragStarted] = useState(false);
    const containerRef = useRef(null);

    const updateClipPath = useCallback((percentage) => {
        const generatedImg = containerRef.current?.querySelector('.generated-image');
        if (generatedImg) {
            generatedImg.style.clipPath = `polygon(0 0, 100% 0, 100% ${percentage}%, 0 ${percentage}%)`;
        }
    }, []);

    useEffect(() => {
        updateClipPath(sliderValue);
    }, [sliderValue, updateClipPath]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragStarted(false);
    };

        const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;
        
        setDragStarted(true);
        const rect = containerRef.current.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const percentage = Math.max(0, Math.min(100, (y / rect.height) * 100));
        setSliderValue(percentage);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleCardClick = (e) => {
        // Prevent navigation if the user was dragging the slider
        if (dragStarted) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    return (
        <Link to={`/map/${map.id}`} className="map-card" onClick={handleCardClick}>
            <div className="map-image-container" ref={containerRef}>
                {map.originalImageUrl && (
                    <img
                        src={map.originalImageUrl}
                        alt="Original map"
                        className="original-image"
                        loading="lazy"
                    />
                )}
                {map.generatedImageUrl && (
                    <img
                        src={map.generatedImageUrl}
                        alt="Generated map"
                        className="generated-image"
                        loading="lazy"
                    />
                )}
                <div
                    className="slider-control"
                    onMouseDown={handleMouseDown}
                    style={{ cursor: isDragging ? 'ns-resize' : 'grab' }}
                >
                    <div className="slider-line" style={{ top: `${sliderValue}%` }}>
                        <div className="slider-handle"></div>
                    </div>
                </div>
            </div>
            <div className="map-info">
                <h3>{map.activityName}</h3>
                <div className="map-stats">
                    <span>{formatDistance(map.distance)}</span>
                    <span>•</span>
                    <span>{formatElevation(map.elevationGain)}</span>
                    <span>•</span>
                    <span>{formatDuration(map.movingTime)}</span>
                </div>
                <p className="map-style">{map.style}</p>
            </div>
        </Link>
    );
}

function GalleryPage() {
    const { t } = useTranslation();
    const [maps, setMaps] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [lastDocId, setLastDocId] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const navigate = useNavigate();

    const loadMaps = async (loadMore = false) => {
        if (loadMore) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const getPublicMapsFunc = httpsCallable(functions, 'getPublicMaps');
            const result = await getPublicMapsFunc({
                lastDocId: loadMore ? lastDocId : null,
                limit: 20
            });

            if (loadMore) {
                setMaps(prev => [...prev, ...result.data.maps]);
            } else {
                setMaps(result.data.maps);
            }

            setHasMore(result.data.hasMore);
            setLastDocId(result.data.lastDocId);
            setError(null);
        } catch (error) {
            console.error('Error loading gallery:', error);
            setError('Failed to load gallery. Please try again later.');
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    // Check for admin status
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    if (idTokenResult.claims.admin) {
                        setIsAdmin(true);
                        setIsCheckingAuth(false);
                    } else {
                        // Not an admin, redirect to home
                        navigate('/');
                    }
                } catch (error) {
                    console.error("Error checking admin status:", error);
                    navigate('/');
                }
            } else {
                // Not authenticated, redirect to home
                navigate('/');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Load maps only if admin
    useEffect(() => {
        if (isAdmin) {
            loadMaps();
        }
    }, [isAdmin]);

    const handleLoadMore = () => {
        if (!isLoadingMore && hasMore) {
            loadMaps(true);
        }
    };

    if (isCheckingAuth || isLoading) {
        return (
            <div className="gallery-page">
                <div className="container">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>{isCheckingAuth ? t('gallery.checking', 'Checking permissions...') : t('gallery.loading', 'Loading gallery...')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gallery-page">
                <div className="container">
                    <div className="error-state">
                        <i className="ti ti-alert-circle"></i>
                        <p>{error}</p>
                        <button onClick={() => loadMaps()} className="retry-button">
                            {t('gallery.tryAgain', 'Try Again')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="gallery-page">
            <div className="container">
                <header className="gallery-header">
                    <h1>{t('gallery.title', 'Mythic Maps Gallery')}</h1>
                    <p>{t('gallery.subtitle', 'Explore epic adventures transformed into art')}</p>
                </header>

                {maps.length === 0 ? (
                    <div className="empty-state">
                        <i className="ti ti-photo-off"></i>
                        <p>{t('gallery.empty1', 'No maps have been shared to the gallery yet.')}</p>
                        <p>{t('gallery.empty2', 'Be the first to share your mythic map!')}</p>
                    </div>
                ) : (
                    <>
                        <div className="maps-grid">
                            {maps.map(map => (
                                <MapCard key={map.id} map={map} />
                            ))}
                        </div>

                        {hasMore && (
                            <div className="load-more-container">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                    className="load-more-button"
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <div className="spinner-small"></div>
                                            {t('gallery.loadingMore', 'Loading...')}
                                        </>
                                    ) : (
                                        t('gallery.loadMore', 'Load More')
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default GalleryPage;